/* eslint-disable camelcase */
import { format } from "date-fns";
import { IEmployeeMutationPayload, IGetUserPositionData } from "../../../services/userPositionService/types";
import { MutationFormFields } from "./components/MutationUser/types";

/**
 * Parses a string to check if it matches the "ID • Name (email)" format.
 * If the string matches, it returns an object containing the NIP, name, and email.
 * @param inputString The string to parse.
 * @returns A `UserInfo` object if the format is correct, or `null` otherwise.
 */
export const parseUserInfoString = (inputString: string): {
  nip: string;
  name: string;
  email: string;
} | null => {
  const regex = /^(.+?)\s•\s(.+?)\s\((.+?)\)$/;
  const match = inputString.match(regex);
  // match[0] is the full matched string.
  // match[1] is the first capture group (NIP).
  // match[2] is the second capture group (Name).
  // match[3] is the third capture group (Email).
  if (match && match.length > 3) {
    return {
      nip: match[1],
      name: match[2],
      email: match[3],
    };
  }

  return null;
};

/**
 * Checks if a string matches the format "ID • Name (email)" and extracts the email.
 * If it doesnt match, it returns the original string.
 * @param inputString The string to check.
 * @param type The type to check.
 * @returns The extracted email or the original string.
 */
export const extractInfoFromFormattedString = (inputString: string, type: "email" | "name" | "nip"): string => {
  const userInfo = parseUserInfoString(inputString);
  return userInfo ? userInfo[type] : inputString;
};

interface IFormatPayloadData extends MutationFormFields {
  targetUser: IGetUserPositionData;
  create_by: string;
  incomingJobTitle: string;
}

/**
 * Converts the raw mutation form data into the required API payload format.
 * @param data The input data object, conforming to the IFormatPayloadData interface.
 * @returns A formatted object conforming to the IEmployeeMutationPayload interface.
 */
export function formatMutationPayload(
  data: IFormatPayloadData,
): IEmployeeMutationPayload {
  const target_department_pools = data.departments.map((dept) => {
    const [poolId = "", department = ""] = String(dept.value).split(".");
    return {
      target_pool_id: poolId,
      target_department: department,
    };
  });

  let target_area_id = "";
  let target_user_pool_id = "";

  if (data.type_mutation === "pool" && data.pools?.length > 0) {
    const areaIds = data.pools.map((pool) => String(pool.value).split(".")[0]);
    const poolIds = data.pools.map((pool) => String(pool.value).split(".")[1]);

    const uniqueAreaIds = [...new Set(areaIds)];

    target_area_id = uniqueAreaIds.join(",");
    target_user_pool_id = poolIds.join(",");
  }

  const formattedPayload: IEmployeeMutationPayload = {
    tipe_mutasi: data.type_mutation === "pool" ? "P" : "D",
    target_employee_nip: data.targetUser.employee_id,
    target_employee_email: data.targetUser.employee_email,
    target_title: data.targetUser.job_title,
    target_department_pools,
    target_area_id,
    target_user_pool_id,

    incoming_user_identity: {
      employee_email: extractInfoFromFormattedString(data.replacement_user!.label, "email"),
      employee_id: extractInfoFromFormattedString(data.replacement_user!.label, "nip"),
      employee_name: extractInfoFromFormattedString(data.replacement_user!.label, "name"),
      job_title: data.incomingJobTitle,
    },
    create_by: data.create_by,
    tanggal_sk: format(new Date(data.date), "yyyy-MM-dd"),
    alasan_mutasi: data.reason,
  };

  return formattedPayload;
}
